import { useEffect, useState } from "react";

import { useAuth } from "./useAuth";
import { useMetadata } from "./query";
import {
    ORG_METADATA_SCOPE,
    resolveOrgEstimatingDefaults,
    type OrgEstimatingDefaults,
} from "../constants/orgSettings";

export default function useOrgEstimatingDefaults() {
    const { selectedOrganization } = useAuth();
    const { useList } = useMetadata();
    const listSvc = useList();

    const [defaults, setDefaults] = useState<OrgEstimatingDefaults | null>(null);
    const [loading, setLoading] = useState(Boolean(selectedOrganization?.Id));
    const [error, setError] = useState<string | null>(null);

    async function loadDefaults(
        organizationId: string,
        isCancelled?: () => boolean,
    ) {
        setLoading(true);
        setError(null);
        try {
            const rows = await listSvc.mutateAsync({
                OrganizationId: organizationId,
                Scope: ORG_METADATA_SCOPE,
                ScopeId: organizationId,
            });
            if (isCancelled?.()) return;
            setDefaults(resolveOrgEstimatingDefaults(rows));
        } catch (e: any) {
            if (isCancelled?.()) return;
            setDefaults(null);
            setError(
                e?.message ||
                    "Organization estimating defaults are not configured. Set them in Admin → General.",
            );
        } finally {
            if (!isCancelled?.()) setLoading(false);
        }
    }

    useEffect(() => {
        const organizationId = selectedOrganization?.Id;
        if (!organizationId) {
            setDefaults(null);
            setError("Select an organization to load estimating defaults.");
            setLoading(false);
            return;
        }

        let cancelled = false;
        void loadDefaults(organizationId, () => cancelled);

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrganization?.Id]);

    return { defaults, loading, error, configured: Boolean(defaults) };
}
